import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || '';

export default function LeadFormPage() {
  const { token, formId } = useParams();
  const [status, setStatus] = useState('loading'); // loading | ready | submitted | error
  const [salesName, setSalesName] = useState('');
  const [formTitle, setFormTitle] = useState('');
  const [fields, setFields] = useState([]);
  const [formData, setFormData] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!token || !formId) {
      setStatus('error');
      return;
    }
    axios.get(`${API}/api/public/lead/${token}/form/${formId}`)
      .then(r => {
        setSalesName(r.data.sales_name);
        setFormTitle(r.data.form_title);
        setFields(r.data.fields || []);
        // Initialize form data with empty values
        const init = {};
        (r.data.fields || []).forEach(f => { init[f.id] = ''; });
        setFormData(init);
        setStatus('ready');
      })
      .catch(() => setStatus('error'));
  }, [token, formId]);

  const validate = () => {
    const e = {};
    fields.forEach(f => {
      if (f.required && !formData[f.id]?.toString().trim()) {
        e[f.id] = `請填寫${f.label}`;
      }
      if (f.type === 'email' && formData[f.id] && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData[f.id])) {
        e[f.id] = '電子郵件格式不正確';
      }
    });
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/public/lead/${token}/form/${formId}`, { data: formData });
      setStatus('submitted');
    } catch (err) {
      const msg = err.response?.data?.error || '提交失敗，請稍後再試';
      setErrors({ _form: msg });
    } finally { setSubmitting(false); }
  };

  const handleReset = () => {
    const init = {};
    fields.forEach(f => { init[f.id] = ''; });
    setFormData(init);
    setErrors({});
    setStatus('ready');
  };

  const update = (fieldId, val) => {
    setFormData(prev => ({ ...prev, [fieldId]: val }));
    if (errors[fieldId]) setErrors(prev => { const n = { ...prev }; delete n[fieldId]; return n; });
  };

  // ─── Loading ───
  if (status === 'loading') {
    return (
      <div style={pageStyle}>
        <div style={{ textAlign: 'center', padding: 60, color: '#94a3b8' }}>
          <div style={spinnerStyle} />
          <p style={{ marginTop: 16, fontSize: 16 }}>載入中...</p>
        </div>
      </div>
    );
  }

  // ─── Invalid token or no formId ───
  if (status === 'error') {
    return (
      <div style={pageStyle}>
        <div style={cardStyle}>
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>😕</div>
            <h2 style={{ margin: 0, fontSize: 20, color: '#0f172a' }}>連結無效</h2>
            <p style={{ margin: '12px 0 0', fontSize: 15, color: '#64748b' }}>
              此填單連結已失效或不存在，請向業務人員索取新連結。
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ─── Thank you ───
  if (status === 'submitted') {
    return (
      <div style={pageStyle}>
        <div style={cardStyle}>
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>✅</div>
            <h2 style={{ margin: 0, fontSize: 22, color: '#0f172a', fontWeight: 700 }}>感謝您的填寫！</h2>
            <p style={{ margin: '12px 0 24px', fontSize: 15, color: '#64748b', lineHeight: 1.6 }}>
              您的資料已成功送出，我們的業務專員將盡快與您聯繫。
            </p>
            <button onClick={handleReset} style={{
              ...btnStyle,
              background: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0',
              fontSize: 15, padding: '14px 32px',
            }}>
              再填一位
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ─── Dynamic Form ───
  return (
    <div style={pageStyle}>
      <div style={cardStyle}>
        {/* Header */}
        <div style={{
          background: 'linear-gradient(135deg, #0c4a3e 0%, #134e4a 100%)',
          margin: '-24px -20px 24px', padding: '28px 24px', borderRadius: '16px 16px 0 0',
        }}>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#fff', letterSpacing: 1 }}>
            {formTitle || '資料填寫'}
          </h1>
          <p style={{ margin: '6px 0 0', fontSize: 13, color: 'rgba(255,255,255,0.7)' }}>
            請填寫您的基本資料{salesName && `，${salesName} 將為您提供專屬服務`}
          </p>
        </div>

        {errors._form && (
          <div style={{
            padding: '12px 14px', background: '#fef2f2', border: '1px solid #fecaca',
            borderRadius: 10, marginBottom: 16, fontSize: 14, color: '#dc2626',
          }}>
            {errors._form}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {fields.map(field => (
            <Field key={field.id} label={field.label} required={field.required}
              error={errors[field.id]} helpText={field.helpText}>
              {renderFieldInput(field, formData[field.id], val => update(field.id, val))}
            </Field>
          ))}

          {/* Submit */}
          <button type="submit" disabled={submitting} style={{
            ...btnStyle,
            background: submitting ? '#94a3b8' : 'linear-gradient(135deg, #0c4a3e 0%, #134e4a 100%)',
            color: '#fff', fontSize: 17, fontWeight: 700, padding: '16px',
            marginTop: 8, width: '100%', letterSpacing: 1,
          }}>
            {submitting ? '送出中...' : '送出資料'}
          </button>
        </form>

        <p style={{ textAlign: 'center', margin: '16px 0 0', fontSize: 11, color: '#cbd5e1' }}>
          您的資料僅供業務服務使用，不會分享給第三方
        </p>
      </div>
    </div>
  );
}

// ─── Dynamic field renderer ───
function renderFieldInput(field, value, onChange) {
  const { type, options = [], placeholder } = field;

  switch (type) {
    case 'select':
      return (
        <select value={value || ''} onChange={e => onChange(e.target.value)} style={inputStyle}>
          <option value="">{placeholder || '請選擇'}</option>
          {options.map((opt, i) => <option key={i} value={opt}>{opt}</option>)}
        </select>
      );

    case 'radio':
      return (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, padding: '4px 0' }}>
          {options.map((opt, i) => (
            <label key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 15, cursor: 'pointer' }}>
              <input type="radio" name={field.id} value={opt} checked={value === opt}
                onChange={e => onChange(e.target.value)} />
              {opt}
            </label>
          ))}
        </div>
      );

    case 'checkbox':
      return (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, padding: '4px 0' }}>
          {options.map((opt, i) => {
            const checked = (value || '').split(',').includes(opt);
            return (
              <label key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 15, cursor: 'pointer' }}>
                <input type="checkbox" checked={checked}
                  onChange={e => {
                    const current = (value || '').split(',').filter(Boolean);
                    if (e.target.checked) current.push(opt);
                    else current.splice(current.indexOf(opt), 1);
                    onChange(current.join(','));
                  }} />
                {opt}
              </label>
            );
          })}
        </div>
      );

    case 'textarea':
      return (
        <textarea value={value || ''} onChange={e => onChange(e.target.value)}
          placeholder={placeholder || ''} style={{ ...inputStyle, minHeight: 80, resize: 'vertical' }} />
      );

    case 'number':
      return (
        <input type="number" value={value || ''} onChange={e => onChange(e.target.value)}
          placeholder={placeholder || ''} style={inputStyle} inputMode="numeric" />
      );

    case 'date':
      return (
        <input type="date" value={value || ''} onChange={e => onChange(e.target.value)}
          style={inputStyle} />
      );

    case 'email':
      return (
        <input type="email" value={value || ''} onChange={e => onChange(e.target.value)}
          placeholder={placeholder || 'example@email.com'} style={inputStyle} inputMode="email" />
      );

    case 'phone':
      return (
        <input type="tel" value={value || ''} onChange={e => onChange(e.target.value)}
          placeholder={placeholder || ''} style={inputStyle} inputMode="tel" />
      );

    default: // text
      return (
        <input type="text" value={value || ''} onChange={e => onChange(e.target.value)}
          placeholder={placeholder || ''} style={inputStyle} />
      );
  }
}

// ─── Field wrapper ───
function Field({ label, required, error, helpText, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{
        display: 'block', fontSize: 14, fontWeight: 600, color: '#334155',
        marginBottom: 6,
      }}>
        {label}{required && <span style={{ color: '#ef4444', marginLeft: 4 }}>*</span>}
      </label>
      {children}
      {helpText && <p style={{ margin: '4px 0 0', fontSize: 12, color: '#94a3b8' }}>{helpText}</p>}
      {error && <p style={{ margin: '4px 0 0', fontSize: 12, color: '#ef4444' }}>{error}</p>}
    </div>
  );
}

// ─── Styles ───
const pageStyle = {
  minHeight: '100dvh',
  background: 'linear-gradient(180deg, #f0fdf4 0%, #f8fafc 50%)',
  display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
  padding: '24px 16px 40px',
  fontFamily: "'Noto Sans TC', -apple-system, BlinkMacSystemFont, sans-serif",
};

const cardStyle = {
  background: '#fff', borderRadius: 16, padding: '24px 20px',
  width: '100%', maxWidth: 480,
  boxShadow: '0 4px 24px rgba(0,0,0,0.08), 0 1px 3px rgba(0,0,0,0.04)',
};

const inputStyle = {
  width: '100%', padding: '14px 14px', fontSize: 16, borderRadius: 10,
  border: '1.5px solid #e2e8f0', outline: 'none', background: '#f8fafc',
  fontFamily: 'inherit', boxSizing: 'border-box',
  WebkitAppearance: 'none', transition: 'border-color 0.2s',
};

const btnStyle = {
  display: 'block', borderRadius: 12, border: 'none', cursor: 'pointer',
  fontFamily: 'inherit', transition: 'opacity 0.2s',
};

const spinnerStyle = {
  width: 36, height: 36, border: '3px solid #e2e8f0', borderTopColor: '#0c4a3e',
  borderRadius: '50%', animation: 'spin 0.8s linear infinite',
  margin: '0 auto',
};
